// ─────────────────────────────────────────────────────────────────────────────
// Fish cosmetics, bought with sand dollars in the shop (routes/student/Shop.tsx):
// a hat, something for the face, and a trail. A student wears one of each; they
// show everywhere their fish does (the ocean, classmates' screens, the class
// game). Drawn by drawAvatar (avatar.ts) in the fish's own units: the body is
// about 2·rx wide and 2·ry tall, centred on (0, 0), facing right.
//
// Prices live in the database too (ocean_buy_gear in
// supabase/migrations/20261028_shop_and_games.sql, made from this list): keep them in step.
// ─────────────────────────────────────────────────────────────────────────────

export type GearSlot = 'hat' | 'face' | 'trail';
export type GearItem = { id: string; slot: GearSlot; name: string; cost: number; icon: string };

export const GEAR: GearItem[] = [
  { id: 'party_hat', slot: 'hat', name: 'Party hat', cost: 12, icon: '🥳' },
  { id: 'beanie', slot: 'hat', name: 'Beanie', cost: 15, icon: '🧢' },
  { id: 'pirate_hat', slot: 'hat', name: 'Pirate hat', cost: 25, icon: '🏴‍☠️' },
  { id: 'top_hat', slot: 'hat', name: 'Top hat', cost: 30, icon: '🎩' },
  { id: 'sombrero', slot: 'hat', name: 'Sombrero', cost: 30, icon: '🪅' },
  { id: 'wizard_hat', slot: 'hat', name: 'Wizard hat', cost: 35, icon: '🧙' },
  { id: 'gold_crown', slot: 'hat', name: 'Golden crown', cost: 60, icon: '👑' },
  { id: 'flower_crown', slot: 'hat', name: 'Flower crown', cost: 18, icon: '🌸' },
  { id: 'cowboy_hat', slot: 'hat', name: 'Cowboy hat', cost: 20, icon: '🤠' },
  { id: 'chef_hat', slot: 'hat', name: 'Chef hat', cost: 22, icon: '👨‍🍳' },
  { id: 'viking_helmet', slot: 'hat', name: 'Viking helmet', cost: 40, icon: '⚔️' },
  { id: 'shades', slot: 'face', name: 'Sunglasses', cost: 10, icon: '🕶️' },
  { id: 'mustache', slot: 'face', name: 'Mustache', cost: 12, icon: '🥸' },
  { id: 'heart_glasses', slot: 'face', name: 'Heart glasses', cost: 15, icon: '😍' },
  { id: 'monocle', slot: 'face', name: 'Monocle', cost: 20, icon: '🧐' },
  { id: 'star_glasses', slot: 'face', name: 'Star glasses', cost: 25, icon: '🤩' },
  { id: 'clown_nose', slot: 'face', name: 'Clown nose', cost: 10, icon: '🔴' },
  { id: 'eyepatch', slot: 'face', name: 'Eye patch', cost: 12, icon: '🏴‍☠️' },
  { id: 'snorkel', slot: 'face', name: 'Snorkel mask', cost: 18, icon: '🤿' },
  { id: 'bubble_trail', slot: 'trail', name: 'Bubble trail', cost: 20, icon: '🫧' },
  { id: 'sparkle_trail', slot: 'trail', name: 'Sparkle trail', cost: 30, icon: '✨' },
  { id: 'heart_trail', slot: 'trail', name: 'Heart trail', cost: 30, icon: '💕' },
  { id: 'rainbow_trail', slot: 'trail', name: 'Rainbow trail', cost: 45, icon: '🌈' },
  { id: 'music_trail', slot: 'trail', name: 'Music trail', cost: 30, icon: '🎵' },
  { id: 'star_trail', slot: 'trail', name: 'Star trail', cost: 35, icon: '⭐' },
  { id: 'fire_trail', slot: 'trail', name: 'Fire trail', cost: 40, icon: '🔥' },
];
export const gearById = (id: string | undefined) => GEAR.find(g => g.id === id);

export type Gear = Partial<Record<GearSlot, string>>;

/** Only real items, each in its own slot. */
export function cleanGear(x: unknown): Gear | undefined {
  if (!x || typeof x !== 'object') return undefined;
  const out: Gear = {};
  for (const slot of ['hat', 'face', 'trail'] as GearSlot[]) {
    const id = (x as Record<string, unknown>)[slot];
    if (typeof id === 'string' && gearById(id)?.slot === slot) out[slot] = id;
  }
  return Object.keys(out).length ? out : undefined;
}

const INK = '#1e2633';

/** The trail, streaming back from the tail (drawn first, behind the fish). */
export function drawTrail(g: CanvasRenderingContext2D, id: string, rx: number, t: number) {
  g.save();
  g.translate(-rx, 0); g.scale(1.3, 1.3); g.translate(rx, 0); g.lineWidth /= 1.3;
  for (let k = 0; k < 6; k++) {
    const p = (t * 1.2 + k / 6) % 1;              // 0 at the tail → 1 far behind
    const x = -rx - 0.35 - p * 1.05, y = Math.sin(t * 3 + k * 1.7) * 0.18 + (k % 2 ? 0.1 : -0.1);
    g.globalAlpha = 1 - p;
    if (id === 'bubble_trail') {
      g.strokeStyle = 'rgba(255,255,255,0.9)'; g.lineWidth = 0.04;
      g.beginPath(); g.arc(x, y, 0.07 + p * 0.06, 0, Math.PI * 2); g.stroke();
    } else if (id === 'sparkle_trail') {
      g.fillStyle = k % 2 ? '#fff6a8' : '#ffffff';
      const r = 0.12 * (1 - p * 0.5);
      g.beginPath(); for (let i = 0; i < 8; i++) { const a = (i * Math.PI) / 4, rr = i % 2 ? r * 0.35 : r; g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); } g.closePath(); g.fill();
    } else if (id === 'heart_trail') {
      g.fillStyle = k % 2 ? '#ff5c8a' : '#ff9ecb';
      const s = 0.09 * (1 - p * 0.4);
      g.beginPath(); g.moveTo(x, y + s); g.bezierCurveTo(x - s * 1.6, y - s * 0.4, x - s * 0.6, y - s * 1.5, x, y - s * 0.5);
      g.bezierCurveTo(x + s * 0.6, y - s * 1.5, x + s * 1.6, y - s * 0.4, x, y + s); g.fill();
    } else if (id === 'music_trail') {
      g.fillStyle = k % 2 ? '#6f4bd8' : '#1e2633';
      const sz = 0.09 * (1 - p * 0.4);
      g.beginPath(); g.ellipse(x, y + sz, sz, sz * 0.75, -0.4, 0, Math.PI * 2); g.fill();
      g.fillRect(x + sz * 0.8, y - sz * 2.2, sz * 0.25, sz * 3.1);
      if (k % 2) g.fillRect(x + sz * 0.8, y - sz * 2.2, sz * 1.2, sz * 0.4);
    } else if (id === 'star_trail') {
      g.fillStyle = ['#ffd23d', '#fff6a8', '#ffb347'][k % 3];
      const r = 0.12 * (1 - p * 0.5);
      g.beginPath(); for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.45 : r; g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); } g.closePath(); g.fill();
    } else if (id === 'fire_trail') {
      g.fillStyle = ['#ff5a1f', '#ffb347', '#ffd23d'][k % 3];
      const r = 0.12 * (1 - p * 0.6);
      g.beginPath(); g.moveTo(x - r, y); g.quadraticCurveTo(x - r * 0.2, y - r * 2.4, x + r * 0.4, y - r * 0.6); g.quadraticCurveTo(x + r, y + r, x, y + r); g.quadraticCurveTo(x - r, y + r * 0.8, x - r, y); g.fill();
    } else if (id === 'rainbow_trail') {
      g.fillStyle = ['#ff5f6d', '#ffc371', '#fff36b', '#5dfc8d', '#4fc3ff', '#b57bff'][k % 6];
      g.beginPath(); g.arc(x, y * 0.5, 0.1 * (1 - p * 0.5), 0, Math.PI * 2); g.fill();
    }
  }
  g.restore();
}

/** A hat on top of the head. (cx, top): where the top of the head is. */
export function drawHat(g: CanvasRenderingContext2D, id: string, cx: number, top: number, t: number) {
  g.save();
  // Drawn small, then grown about the top of the head (keeping the outline's width).
  const k = 1.6;
  g.translate(cx, top); g.scale(k, k); g.translate(-cx, -top); g.lineWidth /= k;
  g.lineJoin = 'round'; g.strokeStyle = INK;
  const y = top + 0.04;
  switch (id) {
    case 'party_hat':
      g.fillStyle = '#ff5c8a';
      g.beginPath(); g.moveTo(cx - 0.22, y); g.lineTo(cx + 0.02, y - 0.62); g.lineTo(cx + 0.24, y); g.closePath(); g.fill(); g.stroke();
      g.strokeStyle = '#ffd23d'; g.lineWidth *= 1.6;
      for (const k of [0.3, 0.6]) { g.beginPath(); g.moveTo(cx - 0.22 + k * 0.22, y - k * 0.62 + 0.1); g.lineTo(cx + 0.24 - k * 0.22, y - k * 0.62 + 0.1); g.stroke(); }
      g.lineWidth /= 1.6; g.strokeStyle = INK;
      g.fillStyle = '#ffd23d'; g.beginPath(); g.arc(cx + 0.02, y - 0.66, 0.08, 0, Math.PI * 2); g.fill(); g.stroke();
      break;
    case 'beanie':
      g.fillStyle = '#3db8ff';
      g.beginPath(); g.moveTo(cx - 0.36, y + 0.04); g.quadraticCurveTo(cx - 0.34, y - 0.46, cx, y - 0.48); g.quadraticCurveTo(cx + 0.34, y - 0.46, cx + 0.36, y + 0.04); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#f4f4f4'; g.beginPath(); g.roundRect(cx - 0.38, y - 0.06, 0.76, 0.14, 0.05); g.fill(); g.stroke();
      g.fillStyle = '#ff5c8a'; g.beginPath(); g.arc(cx, y - 0.52, 0.09, 0, Math.PI * 2); g.fill(); g.stroke();
      break;
    case 'pirate_hat':
      g.fillStyle = '#2c2f3a';
      g.beginPath(); g.moveTo(cx - 0.5, y); g.quadraticCurveTo(cx - 0.3, y - 0.5, cx, y - 0.42); g.quadraticCurveTo(cx + 0.3, y - 0.5, cx + 0.5, y); g.quadraticCurveTo(cx, y - 0.12, cx - 0.5, y); g.fill(); g.stroke();
      g.fillStyle = '#fff'; g.beginPath(); g.arc(cx, y - 0.24, 0.07, 0, Math.PI * 2); g.fill();
      g.strokeStyle = '#fff'; g.beginPath(); g.moveTo(cx - 0.08, y - 0.12); g.lineTo(cx + 0.08, y - 0.06); g.moveTo(cx + 0.08, y - 0.12); g.lineTo(cx - 0.08, y - 0.06); g.stroke();
      break;
    case 'top_hat':
      g.fillStyle = '#2c2f3a';
      g.beginPath(); g.roundRect(cx - 0.36, y - 0.06, 0.72, 0.1, 0.04); g.fill(); g.stroke();
      g.beginPath(); g.roundRect(cx - 0.22, y - 0.58, 0.44, 0.54, 0.04); g.fill(); g.stroke();
      g.fillStyle = '#f25c7a'; g.fillRect(cx - 0.22, y - 0.18, 0.44, 0.08);
      break;
    case 'sombrero':
      g.fillStyle = '#f6c453';
      g.beginPath(); g.ellipse(cx, y, 0.62, 0.12, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(cx - 0.24, y - 0.02); g.quadraticCurveTo(cx - 0.2, y - 0.5, cx, y - 0.5); g.quadraticCurveTo(cx + 0.2, y - 0.5, cx + 0.24, y - 0.02); g.closePath(); g.fill(); g.stroke();
      g.strokeStyle = '#e8572a'; g.lineWidth *= 2; g.beginPath(); g.moveTo(cx - 0.23, y - 0.12); g.lineTo(cx + 0.23, y - 0.12); g.stroke();
      break;
    case 'wizard_hat': {
      g.fillStyle = '#6f4bd8';
      g.beginPath(); g.ellipse(cx, y, 0.42, 0.09, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(cx - 0.26, y - 0.02); g.quadraticCurveTo(cx - 0.05, y - 0.5, cx + 0.3 + Math.sin(t * 2) * 0.04, y - 0.8); g.quadraticCurveTo(cx + 0.05, y - 0.4, cx + 0.26, y - 0.02); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#ffd23d';
      for (const [sx, sy] of [[-0.08, -0.22], [0.08, -0.42]]) { g.beginPath(); for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 5, r = i % 2 ? 0.025 : 0.06; g.lineTo(cx + sx + Math.cos(a) * r, y + sy + Math.sin(a) * r); } g.closePath(); g.fill(); }
      break;
    }
    case 'flower_crown':
      g.strokeStyle = '#3fae5a'; g.lineWidth *= 1.6;
      g.beginPath(); g.ellipse(cx, y - 0.02, 0.34, 0.08, 0, Math.PI, 0); g.stroke();
      g.lineWidth /= 1.6; g.strokeStyle = INK;
      for (const [k, col] of [[-0.3, '#ff6fa8'], [-0.12, '#ffd23d'], [0.06, '#b57bff'], [0.24, '#ff9ecb']] as [number, string][]) {
        g.fillStyle = col;
        for (let i = 0; i < 5; i++) { const a = (i / 5) * Math.PI * 2; g.beginPath(); g.arc(cx + k + Math.cos(a) * 0.05, y - 0.1 + Math.sin(a) * 0.05, 0.045, 0, Math.PI * 2); g.fill(); }
        g.fillStyle = '#fff6a8'; g.beginPath(); g.arc(cx + k, y - 0.1, 0.03, 0, Math.PI * 2); g.fill();
      }
      break;
    case 'cowboy_hat':
      g.fillStyle = '#a0703f';
      g.beginPath(); g.moveTo(cx - 0.5, y - 0.04); g.quadraticCurveTo(cx, y + 0.12, cx + 0.5, y - 0.04); g.quadraticCurveTo(cx + 0.55, y - 0.14, cx + 0.42, y - 0.1); g.quadraticCurveTo(cx, y - 0.02, cx - 0.42, y - 0.1); g.quadraticCurveTo(cx - 0.55, y - 0.14, cx - 0.5, y - 0.04); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(cx - 0.24, y - 0.06); g.quadraticCurveTo(cx - 0.26, y - 0.44, cx - 0.08, y - 0.4); g.quadraticCurveTo(cx, y - 0.32, cx + 0.08, y - 0.4); g.quadraticCurveTo(cx + 0.26, y - 0.44, cx + 0.24, y - 0.06); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#5a3a24'; g.fillRect(cx - 0.24, y - 0.14, 0.48, 0.06);
      break;
    case 'chef_hat':
      g.fillStyle = '#ffffff';
      g.beginPath(); g.roundRect(cx - 0.2, y - 0.2, 0.4, 0.22, 0.03); g.fill(); g.stroke();
      for (const [dx, dy, r] of [[-0.16, -0.32, 0.14], [0, -0.4, 0.17], [0.16, -0.32, 0.14]]) { g.beginPath(); g.arc(cx + dx, y + dy, r, 0, Math.PI * 2); g.fill(); g.stroke(); }
      g.fillRect(cx - 0.19, y - 0.3, 0.38, 0.1);
      break;
    case 'viking_helmet':
      g.fillStyle = '#f4f4f4';
      for (const sd of [-1, 1]) { g.beginPath(); g.moveTo(cx + sd * 0.26, y - 0.18); g.quadraticCurveTo(cx + sd * 0.5, y - 0.3, cx + sd * 0.46, y - 0.62); g.quadraticCurveTo(cx + sd * 0.36, y - 0.34, cx + sd * 0.2, y - 0.28); g.closePath(); g.fill(); g.stroke(); }
      g.fillStyle = '#9aa4b2';
      g.beginPath(); g.moveTo(cx - 0.32, y + 0.02); g.quadraticCurveTo(cx - 0.32, y - 0.44, cx, y - 0.44); g.quadraticCurveTo(cx + 0.32, y - 0.44, cx + 0.32, y + 0.02); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#c9a227'; g.fillRect(cx - 0.32, y - 0.08, 0.64, 0.08); g.strokeRect(cx - 0.32, y - 0.08, 0.64, 0.08);
      break;
    case 'gold_crown':
      g.fillStyle = '#ffd23d';
      g.beginPath(); g.moveTo(cx - 0.34, y + 0.04); g.lineTo(cx - 0.38, y - 0.36); g.lineTo(cx - 0.18, y - 0.16); g.lineTo(cx, y - 0.44); g.lineTo(cx + 0.18, y - 0.16); g.lineTo(cx + 0.38, y - 0.36); g.lineTo(cx + 0.34, y + 0.04); g.closePath(); g.fill(); g.stroke();
      for (const [jx, col] of [[-0.18, '#f25c7a'], [0, '#3db8ff'], [0.18, '#6ad46a']] as [number, string][]) { g.fillStyle = col; g.beginPath(); g.arc(cx + jx, y - 0.06, 0.05, 0, Math.PI * 2); g.fill(); }
      g.fillStyle = `rgba(255,255,255,${0.5 + 0.5 * Math.sin(t * 3)})`; g.beginPath(); g.arc(cx - 0.2, y - 0.3, 0.03, 0, Math.PI * 2); g.fill();
      break;
  }
  g.restore();
}

/** Something on the face, at the eye (ex, ey) with its radius er; the mouth at (mx, my). */
export function drawFace(g: CanvasRenderingContext2D, id: string, ex: number, ey: number, er: number, mx: number, my: number) {
  g.save();
  g.lineJoin = 'round'; g.strokeStyle = INK;
  const r = er + 0.08;
  switch (id) {
    case 'shades':
      g.fillStyle = '#1e2633';
      g.beginPath(); g.roundRect(ex - r, ey - r * 0.7, r * 2, r * 1.4, r * 0.4); g.fill();
      g.beginPath(); g.moveTo(ex - r, ey - r * 0.3); g.lineTo(ex - r - 0.3, ey - r * 0.4); g.stroke();
      g.fillStyle = 'rgba(255,255,255,0.35)'; g.beginPath(); g.roundRect(ex - r * 0.6, ey - r * 0.5, r * 0.5, r * 0.25, 0.03); g.fill();
      break;
    case 'heart_glasses': {
      g.fillStyle = '#ff5c8a';
      const s = r * 0.85;
      g.beginPath(); g.moveTo(ex, ey + s); g.bezierCurveTo(ex - s * 1.7, ey - s * 0.3, ex - s * 0.7, ey - s * 1.5, ex, ey - s * 0.45);
      g.bezierCurveTo(ex + s * 0.7, ey - s * 1.5, ex + s * 1.7, ey - s * 0.3, ex, ey + s); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(ex - s * 1.1, ey - s * 0.2); g.lineTo(ex - s * 1.1 - 0.25, ey - s * 0.3); g.stroke();
      break;
    }
    case 'monocle':
      g.strokeStyle = '#d4a82b'; g.lineWidth *= 1.8;
      g.beginPath(); g.arc(ex, ey, r, 0, Math.PI * 2); g.stroke();
      g.lineWidth /= 1.8; g.beginPath(); g.moveTo(ex - r * 0.5, ey + r * 0.85); g.quadraticCurveTo(ex - r, ey + r * 2.2, ex - r * 0.2, ey + r * 2.8); g.stroke();
      break;
    case 'star_glasses': {
      g.fillStyle = '#ffd23d';
      g.beginPath(); for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.55 : r * 1.25; g.lineTo(ex + Math.cos(a) * rr, ey + Math.sin(a) * rr); } g.closePath(); g.fill(); g.stroke();
      g.fillStyle = 'rgba(30,38,51,0.75)'; g.beginPath(); g.arc(ex, ey, r * 0.45, 0, Math.PI * 2); g.fill();
      break;
    }
    case 'clown_nose':
      g.fillStyle = '#ff3b3b';
      g.beginPath(); g.arc(mx + 0.06, my - 0.14, 0.15, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillStyle = 'rgba(255,255,255,0.7)'; g.beginPath(); g.arc(mx + 0.01, my - 0.19, 0.045, 0, Math.PI * 2); g.fill();
      break;
    case 'eyepatch':
      g.fillStyle = '#1e2633';
      g.beginPath(); g.ellipse(ex, ey, r * 0.9, r * 0.8, 0, 0, Math.PI * 2); g.fill();
      g.beginPath(); g.moveTo(ex - r * 2.2, ey - r * 1.4); g.lineTo(ex + r * 1.6, ey + r * 0.2); g.lineWidth *= 1.4; g.stroke(); g.lineWidth /= 1.4;
      break;
    case 'snorkel':
      g.fillStyle = 'rgba(160,220,255,0.55)'; g.lineWidth *= 1.5; g.strokeStyle = '#ff8a3d';
      g.beginPath(); g.roundRect(ex - r * 1.1, ey - r * 0.9, r * 2.2, r * 1.8, r * 0.5); g.fill(); g.stroke();
      g.strokeStyle = '#3db8ff'; g.beginPath(); g.moveTo(ex - r * 1.2, ey + r * 0.4); g.quadraticCurveTo(ex - r * 2.4, ey + r * 0.6, ex - r * 2.3, ey - r * 2.6); g.stroke();
      g.lineWidth /= 1.5; g.strokeStyle = INK;
      break;
    case 'mustache':
      g.fillStyle = '#4a3324';
      for (const s of [-1, 1]) {
        g.beginPath(); g.moveTo(mx - 0.08, my - 0.1);
        g.quadraticCurveTo(mx - 0.08 + s * 0.2, my - 0.24, mx - 0.08 + s * 0.3, my - 0.08);
        g.quadraticCurveTo(mx - 0.08 + s * 0.16, my - 0.06, mx - 0.08, my - 0.06); g.closePath(); g.fill(); g.stroke();
      }
      break;
  }
  g.restore();
}
